/**
 * Normalizador compartido del campo `accounting` de files/{id}.
 * Ver spec docs/superpowers/specs/2026-07-16-contabilizaciones-multiples-design.md
 *
 * El campo admite dos formatos:
 *  - Legacy: una DocumentReference suelta (documentos previos al cambio).
 *  - Actual: array de DocumentReferences.
 *
 * Módulo puro (sin React/Redux/Firestore) — única fuente de verdad para
 * leer/mutar la lista; los escritores convierten a array en la primera
 * escritura y borran el campo cuando la lista queda vacía.
 */

// Una "ref" válida es cualquier objeto con .get función (DocumentReference
// del SDK cliente). Los strings sembrados por admin SDK no son operables
// aquí y se descartan (mismo criterio defensivo que folder_ref.js).
const isRef = (v) => !!v && typeof v === 'object' && typeof v.get === 'function';

/** Normaliza el campo a lista: null → [], ref suelta → [ref], array → filtrado. */
export const toAccountingList = (value) => {
  if (Array.isArray(value)) return value.filter(isRef);
  return isRef(value) ? [value] : [];
};

/** ¿El documento tiene al menos una contabilización? */
export const hasAccounting = (value) => toAccountingList(value).length > 0;

/**
 * Una sola contabilización por documento (reunión con las supervisoras,
 * 2026-09-18). Las auxiliares subían otra en vez de pedir que se borrara la
 * errada, y el cliente veía dos causaciones de la misma factura. Los
 * soportes adicionales (cajas menores, documento DIAN) van en Vinculados.
 *
 * Los documentos que ya tenían varias las conservan; solo no se les agrega
 * otra. Cambiar la de un documento es: eliminarla con token (queda en la
 * papelera) y subir la nueva.
 */
export const ONE_ACCOUNTING_MESSAGE =
  'Este documento ya tiene su contabilización. Para cambiarla, elimínala (con token) y sube la nueva; los soportes adicionales van en Vinculados.';

/** Mensaje de bloqueo si el documento ya tiene contabilización, o null si se puede subir. */
export const accountingUploadBlock = (value) => (hasAccounting(value) ? ONE_ACCOUNTING_MESSAGE : null);

/** Lista normalizada sin la ref cuyo path coincide con `target`. */
export const removeAccountingRef = (value, target) => {
  const list = toAccountingList(value);
  if (!target?.path) return list;
  return list.filter((r) => r.path !== target.path);
};
